"use strict"

const STORAGE_CONSENT_KEY = "vm_consent_v2"
const STORAGE_CONSENT_VERSION = 2
const STORAGE_CONSENT_MAX_AGE_SECONDS = 31_536_000
const STORAGE_CONSENT_CLOCK_SKEW_SECONDS = 300
const STORAGE_CONSENT_ROOT_DOMAIN = ".vectormethods.com"
const STORAGE_CONSENT_EVENT = "vectormethods:storage-consent"
const consentListeners = new Set()
let observedConsent = undefined
let stopConsentObservation = null

function createStorageConsentRecord(selection, updatedAt = new Date().toISOString()) {
  if (
    !selection
    || typeof selection.analytics !== "boolean"
    || typeof selection.marketing !== "boolean"
  ) {
    throw new TypeError("storage consent selection must contain boolean analytics and marketing values")
  }

  return {
    v: STORAGE_CONSENT_VERSION,
    essential: true,
    analytics: selection.analytics,
    marketing: selection.marketing,
    updatedAt,
  }
}

function encodeStorageConsent(record) {
  return encodeURIComponent(JSON.stringify(record))
}

function parseStorageConsent(value, nowMs = Date.now()) {
  if (!value) return null

  try {
    const parsed = JSON.parse(decodeURIComponent(value))
    const updatedAtMs = typeof parsed.updatedAt === "string"
      ? Date.parse(parsed.updatedAt)
      : Number.NaN
    const ageMs = nowMs - updatedAtMs
    if (
      parsed.v !== STORAGE_CONSENT_VERSION
      || parsed.essential !== true
      || typeof parsed.analytics !== "boolean"
      || typeof parsed.marketing !== "boolean"
      || !Number.isFinite(updatedAtMs)
      || ageMs > STORAGE_CONSENT_MAX_AGE_SECONDS * 1_000
      || ageMs < -STORAGE_CONSENT_CLOCK_SKEW_SECONDS * 1_000
    ) {
      return null
    }

    return {
      v: STORAGE_CONSENT_VERSION,
      essential: true,
      analytics: parsed.analytics,
      marketing: parsed.marketing,
      updatedAt: new Date(updatedAtMs).toISOString(),
    }
  } catch {
    return null
  }
}

function readCookieValue(cookieHeader, name) {
  const prefix = `${name}=`
  for (const segment of String(cookieHeader || "").split(";")) {
    const candidate = segment.trim()
    if (candidate.startsWith(prefix)) {
      return candidate.slice(prefix.length)
    }
  }
  return null
}

function isVectorMethodsHostname(hostname) {
  const normalized = String(hostname || "").trim().toLowerCase().replace(/\.$/, "")
  return normalized === "vectormethods.com" || normalized.endsWith(".vectormethods.com")
}

function buildStorageConsentCookie(
  value,
  hostname,
  isHttps,
  maxAgeSeconds = STORAGE_CONSENT_MAX_AGE_SECONDS,
) {
  const attributes = [
    `${STORAGE_CONSENT_KEY}=${value}`,
    "Path=/",
    `Max-Age=${maxAgeSeconds}`,
    "SameSite=Lax",
  ]
  if (isHttps) attributes.push("Secure")
  if (isVectorMethodsHostname(hostname)) {
    attributes.push(`Domain=${STORAGE_CONSENT_ROOT_DOMAIN}`)
  }
  return attributes.join("; ")
}

function readBrowserStorageConsent() {
  if (typeof window === "undefined") return null
  const cookie = parseStorageConsent(readCookieValue(document.cookie, STORAGE_CONSENT_KEY))
  if (cookie) {
    mirrorBrowserStorageConsent(cookie)
    return cookie
  }
  // On our HTTPS origins the root cookie is authoritative. A stale origin-local
  // mirror must not resurrect a choice withdrawn or removed on another subdomain.
  if (isVectorMethodsHostname(window.location.hostname)) return null
  try {
    return parseStorageConsent(window.localStorage.getItem(STORAGE_CONSENT_KEY))
  } catch {
    return null
  }
}

function mirrorBrowserStorageConsent(record) {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(STORAGE_CONSENT_KEY, encodeStorageConsent(record))
  } catch {
    // The root cookie remains usable when local storage is disabled.
  }
}

function synchronizeStorageConsent() {
  const record = readBrowserStorageConsent()
  const signature = JSON.stringify(record)
  if (signature === observedConsent) return record
  observedConsent = signature
  for (const listener of consentListeners) listener(record)
  if (typeof window !== "undefined") {
    const effective = record || createStorageConsentRecord({ analytics: false, marketing: false })
    window.dispatchEvent(new CustomEvent(STORAGE_CONSENT_EVENT, {
      detail: {
        consent: effective,
        consentConfigured: record !== null,
        optionalStorageAllowed: effective.analytics || effective.marketing,
        analyticsStorageAllowed: effective.analytics,
        marketingStorageAllowed: effective.marketing,
      },
    }))
  }
  return record
}

function writeBrowserStorageConsent(record) {
  if (typeof window === "undefined") return
  document.cookie = buildStorageConsentCookie(
    encodeStorageConsent(record), window.location.hostname, window.location.protocol === "https:",
  )
  mirrorBrowserStorageConsent(record)
  synchronizeStorageConsent()
}

function subscribeStorageConsent(listener) {
  if (typeof window === "undefined") return () => {}
  if (consentListeners.size === 0) {
    observedConsent = JSON.stringify(readBrowserStorageConsent())
    const sync = () => synchronizeStorageConsent()
    const onVisibility = () => { if (document.visibilityState === "visible") sync() }
    const onStorage = (event) => { if (event.key === STORAGE_CONSENT_KEY || event.key === null) sync() }
    const onCookieChange = (event) => {
      if ([...(event.changed || []), ...(event.deleted || [])].some((cookie) => cookie.name === STORAGE_CONSENT_KEY)) sync()
    }
    window.addEventListener("storage", onStorage)
    window.addEventListener("pageshow", sync)
    window.addEventListener("focus", sync)
    document.addEventListener("visibilitychange", onVisibility)
    window.cookieStore?.addEventListener("change", onCookieChange)
    stopConsentObservation = () => {
      window.removeEventListener("storage", onStorage)
      window.removeEventListener("pageshow", sync)
      window.removeEventListener("focus", sync)
      document.removeEventListener("visibilitychange", onVisibility)
      window.cookieStore?.removeEventListener("change", onCookieChange)
    }
  }
  consentListeners.add(listener)
  listener(readBrowserStorageConsent())
  return () => {
    consentListeners.delete(listener)
    if (consentListeners.size === 0) {
      stopConsentObservation?.()
      stopConsentObservation = null
    }
  }
}

module.exports = {
  STORAGE_CONSENT_KEY,
  STORAGE_CONSENT_VERSION,
  STORAGE_CONSENT_MAX_AGE_SECONDS,
  STORAGE_CONSENT_CLOCK_SKEW_SECONDS,
  STORAGE_CONSENT_ROOT_DOMAIN,
  STORAGE_CONSENT_EVENT,
  buildStorageConsentCookie,
  createStorageConsentRecord,
  encodeStorageConsent,
  isVectorMethodsHostname,
  parseStorageConsent,
  readCookieValue,
  readBrowserStorageConsent,
  mirrorBrowserStorageConsent,
  writeBrowserStorageConsent,
  synchronizeStorageConsent,
  subscribeStorageConsent,
}
